import { useState, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaEllipsisV,
  FaTrashAlt,
  FaUndo,
  FaChevronDown,
  FaMap,
} from 'react-icons/fa';

/* ===== Helpers Europe/Paris côté front ===== */
function parisDateToParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
  return { y: Number(map.year), m: Number(map.month), d: Number(map.day) };
}
function daysFromTodayParis(dateLike) {
  if (!dateLike) return null;
  const now = new Date();
  const t = new Date(dateLike);
  const { y, m, d } = parisDateToParts(now);
  const { y: y2, m: m2, d: d2 } = parisDateToParts(t);
  const todayUTC = Date.UTC(y, m - 1, d);
  const targetUTC = Date.UTC(y2, m2 - 1, d2);
  const diff = Math.ceil((targetUTC - todayUTC) / (24 * 60 * 60 * 1000));
  return diff;
}
function formatDateLongFR(dateLike) {
  if (!dateLike) return '';
  const d = new Date(dateLike);
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(d);
}
function upcomingSentenceFR(dateLike) {
  const ds = daysFromTodayParis(dateLike);
  const dateStr = formatDateLongFR(dateLike);
  if (ds == null) return '';
  const plural = ds > 1 ? 'jours' : 'jour';
  return `Programmé pour ${dateStr} · Démarre dans ${ds} ${plural}`;
}

/**
 * Props principales :
 * - activityCategories, upcomingCategories, inactiveCategories: Record<catId,{ label,name,totalBroadcasts,emailsToday,totalSent,totalToSend, bySmtpProvider: Record<provId,{name,emailsToday,totalSent,totalToSend,broadcastNames:string[], items?:[{ name, currentDay, totalDays, scheduledStartAt, providerName }]}> }>
 * - global: { broadcastsActiveCount, broadcastsUpcomingCount, broadcastsInactiveCount, emailsToday, totalSent, totalToSend }
 */
function PlaylistCard({
  name,
  playlistId,
  activityCategories = {},
  upcomingCategories = {},
  inactiveCategories = {},
  global = {},
  imagePath = '/img/round.png',
  link,
  deletedAt = null,
  onSoftDelete,
  onRestore,
}) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const hasActive = (global.broadcastsActiveCount || 0) > 0;
  const upcomingCount = global.broadcastsUpcomingCount || 0;
  const inactiveCount = global.broadcastsInactiveCount || 0;
  const isDeleted = !!deletedAt;

  const headerBgClass = isDeleted
    ? 'bg-slate-100'
    : hasActive
      ? 'bg-gradient-to-r from-indigo-50 via-violet-50 to-fuchsia-50'
      : 'bg-slate-50';

  const titleClass = isDeleted
    ? 'line-through text-slate-500'
    : hasActive
      ? 'text-slate-900'
      : 'text-slate-700';

  return (
    <div className="relative group bg-white rounded-2xl overflow-hidden shadow-sm ring-1 mt-5 ring-slate-200 hover:shadow-md transition-shadow">
      {isDeleted && (
        <div className="pointer-events-none absolute -right-14 top-6 rotate-45 bg-rose-600 text-white text-xs font-semibold px-20 py-1 shadow">
          Supprimée
        </div>
      )}

      {/* En-tête */}
      <div
        className={`flex items-center justify-between px-4 sm:px-6 py-4 ${headerBgClass}`}
      >
        <div className="flex items-center gap-4">
          {/* Avatar sans bordure + animation si actifs & non supprimée */}
          <div className="relative">
            <div
              className={
                'relative w-16 h-16 rounded-full overflow-hidden ' +
                (hasActive && !isDeleted
                  ? 'animate-[spin_6s_linear_infinite]'
                  : 'opacity-60 grayscale')
              }
              title={hasActive && !isDeleted ? 'En cours — animé' : undefined}
            >
              <img
                src={imagePath}
                alt="Image Playlist"
                className="w-full h-full object-contain"
                draggable={false}
              />
            </div>
          </div>

          <div>
            <h3 className={`font-bold text-lg ${titleClass}`}>{name}</h3>

            <div className="flex flex-wrap gap-2 mt-1">
              <span
                className={
                  (hasActive && !isDeleted
                    ? 'bg-indigo-100 text-indigo-800'
                    : 'bg-slate-200 text-slate-700') +
                  ' inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full'
                }
              >
                {global.broadcastsActiveCount || 0} actif(s)
              </span>
              <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                {upcomingCount} démarre{upcomingCount > 1 ? 'nt' : ''} bientôt
              </span>
              <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">
                {inactiveCount} inactif(s)
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              navigate(`/playlists/new?view=playlist&id=${playlistId}`)
            }
            className="px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm border transition bg-white hover:bg-emerald-50 border-emerald-200 text-emerald-700 flex items-center gap-1"
            title="Visualiser sur la carte"
          >
            <FaMap size={12} /> Visualiser
          </button>
          <Link to={link || `/broadcasts/playlists/${playlistId}`}>
            <button
              className={
                (hasActive && !isDeleted
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700 border-indigo-600'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-200') +
                ' px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm border transition'
              }
              disabled={isDeleted}
              title={
                isDeleted
                  ? 'Restaurer la playlist pour accéder aux broadcasts'
                  : 'Voir Broadcasts'
              }
            >
              Voir Broadcasts
            </button>
          </Link>

          <div className="relative">
            <button
              onClick={() => setMenuOpen(v => !v)}
              className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <FaEllipsisV />
            </button>
            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-48 rounded-lg border border-slate-200 bg-white shadow-lg z-10 overflow-hidden"
                onMouseLeave={() => setMenuOpen(false)}
              >
                {!isDeleted ? (
                  <button
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onSoftDelete?.();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-rose-700 hover:bg-rose-50"
                    title="Soft delete"
                  >
                    <FaTrashAlt /> Supprimer
                  </button>
                ) : (
                  <button
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onRestore?.();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-emerald-700 hover:bg-emerald-50"
                    title="Restaurer"
                  >
                    <FaUndo /> Restaurer
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Détails par catégories */}
      <div className={'px-4 sm:px-6 pb-5 ' + (isDeleted ? 'opacity-60' : '')}>
        <CategoryAccordion
          title="Broadcasts actifs"
          categories={activityCategories}
          accent="indigo"
          kind="active"
        />
        <CategoryAccordion
          title="Broadcasts qui démarrent bientôt"
          categories={upcomingCategories}
          accent="amber"
          kind="upcoming"
          emptyFallback=""
        />
        <CategoryAccordion
          title="Broadcasts inactifs"
          categories={inactiveCategories}
          accent="slate"
          kind="inactive"
          muted
          emptyFallback=""
        />

        {!hasActive &&
          Object.keys(upcomingCategories || {}).length === 0 &&
          Object.keys(inactiveCategories || {}).length === 0 && (
            <div className="mt-3 text-center text-slate-500 italic">
              Aucun broadcast trouvé pour cette playlist
            </div>
          )}
      </div>
    </div>
  );
}

/** Accordéon réutilisable. `kind`: 'active' | 'upcoming' | 'inactive'. */
const CategoryAccordion = memo(function CategoryAccordion({
  title,
  categories = {},
  accent = 'indigo',
  kind = 'active',
  muted = false,
  emptyFallback,
}) {
  const [openCat, setOpenCat] = useState(null);
  const entries = Object.entries(categories || {});
  if (entries.length === 0) {
    return emptyFallback ? (
      <div className="mt-4 text-center text-slate-500 italic">
        {emptyFallback}
      </div>
    ) : null;
  }

  const openClasses = muted
    ? 'border-slate-300 bg-slate-100'
    : accent === 'indigo'
      ? 'border-indigo-200 bg-indigo-50/40'
      : accent === 'amber'
        ? 'border-amber-200 bg-amber-50/40'
        : 'border-slate-200 bg-slate-50/40';

  const closedClasses = muted
    ? 'border-slate-200 bg-slate-50'
    : 'border-slate-200 bg-white';
  const tableHeadClass = muted
    ? 'bg-slate-50 text-slate-500'
    : 'bg-slate-50 text-slate-700';
  const rowHoverClass = muted
    ? 'hover:bg-slate-50'
    : accent === 'amber'
      ? 'hover:bg-amber-50/50'
      : 'hover:bg-indigo-50/50';

  return (
    <div className="mt-4 space-y-2">
      <div
        className={
          'text-sm font-semibold ' +
          (muted ? 'text-slate-600' : 'text-slate-700')
        }
      >
        {title}
      </div>

      {entries.map(([catId, cat]) => {
        const open = openCat === catId;
        return (
          <div
            key={catId}
            className={
              'border rounded-xl overflow-hidden transition ' +
              (open ? openClasses : closedClasses)
            }
          >
            <button
              onClick={() => setOpenCat(open ? null : catId)}
              className="w-full flex items-center justify-between gap-4 px-3 py-2"
            >
              <div className="flex items-center gap-3">
                <span
                  className={
                    'font-semibold ' +
                    (muted ? 'text-slate-700' : 'text-slate-900')
                  }
                >
                  {cat?.label || cat?.name || 'Non défini'}
                </span>
                <span
                  className={
                    'text-xs ' + (muted ? 'text-slate-500' : 'text-slate-500')
                  }
                >
                  {cat?.totalBroadcasts || 0} broadcast
                  {(cat?.totalBroadcasts || 0) > 1 ? 's' : ''}
                </span>
              </div>
              <div
                className={'transition-transform ' + (open ? 'rotate-180' : '')}
              >
                <FaChevronDown className={muted ? 'text-slate-500' : ''} />
              </div>
            </button>

            <div
              className="grid transition-all"
              style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
            >
              <div className="overflow-hidden">
                <div className="px-3 pb-3">
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className={tableHeadClass}>
                          <th className="text-left border px-4 py-2 text-sm font-semibold">
                            Broadcast
                          </th>
                          <th className="text-left border px-4 py-2 text-sm font-semibold">
                            Provider SMTP
                          </th>
                          {kind === 'upcoming' && (
                            <th className="text-left border px-4 py-2 text-sm font-semibold">
                              Planification
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(cat?.bySmtpProvider || {}).flatMap(
                          ([smtpId, smtp]) => {
                            let items = smtp?.items || [];
                            const namesFallback = (
                              smtp?.broadcastNames || []
                            ).map(n => ({ name: n }));
                            let rows = items.length ? items : namesFallback;

                            // Pour "upcoming", trier par date de démarrage (croissant)
                            if (kind === 'upcoming' && rows.length) {
                              rows = [...rows].sort((a, b) => {
                                const da = a.scheduledStartAt
                                  ? new Date(a.scheduledStartAt).getTime()
                                  : Number.POSITIVE_INFINITY;
                                const db = b.scheduledStartAt
                                  ? new Date(b.scheduledStartAt).getTime()
                                  : Number.POSITIVE_INFINITY;
                                return da - db;
                              });
                            }

                            return rows.map((it, idx) => (
                              <tr
                                key={smtpId + '-' + (it.name || idx)}
                                className={`bg-white border-b last:border-b-0 ${rowHoverClass} transition`}
                              >
                                <td
                                  className={
                                    'px-4 py-2 border font-medium ' +
                                    (muted
                                      ? 'text-slate-700'
                                      : 'text-slate-800')
                                  }
                                >
                                  <div className="flex items-center gap-2">
                                    <span>{it.name}</span>
                                    {kind === 'active' &&
                                    (it.currentDay || it.totalDays) ? (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800">
                                        Jour{' '}
                                        {Math.max(
                                          1,
                                          Number(it.currentDay) || 0
                                        )}{' '}
                                        / {Number(it.totalDays) || 0}
                                      </span>
                                    ) : null}
                                  </div>
                                </td>
                                <td
                                  className={
                                    'px-4 py-2 border ' +
                                    (muted
                                      ? 'text-slate-600'
                                      : 'text-slate-700')
                                  }
                                >
                                  {smtp?.name || it.providerName || '—'}
                                </td>
                                {kind === 'upcoming' && (
                                  <td className="px-4 py-2">
                                    <span className="block text-amber-700">
                                      {upcomingSentenceFR(it.scheduledStartAt)}
                                    </span>
                                  </td>
                                )}
                              </tr>
                            ));
                          }
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});

export default memo(PlaylistCard);
